import { mapActions, mapGetters, mapMutations } from 'vuex'
import { axiosThux } from '@/axiosThux'
import { TYPES as TASK_CATEGORY_TYPES } from '../../task-category/store'
import { TYPES as TASK_PRIORITY_TYPES } from '../../task-priority/store'
import { TYPES as TASK_TAG_TYPES } from '../../task-tag/store'
import { TYPES as USER_TYPES } from '@/pages/account/user/store'
import { TYPES as QUOTATION_TYPES } from '@/pages/quotation/quotation/store'
import TaskChoicesMixin from './TaskChoicesMixin'

const mapResultsToOptions = (listState, labelFn) => {
  if (!listState || !listState.results) return []
  return listState.results
    .filter((item) => item.status !== 0)
    .map((item) => ({ value: item.id, text: labelFn(item), status: item.status, raw: item }))
}

const tokenize = (search) => (search || '').trim().split(/\s+/).filter(Boolean)

const getFieldValue = (item, field) => {
  if (item == null) return ''
  const flat = field.replace(/__/g, '_')
  if (item[flat] != null) return item[flat]
  const parts = field.split('__')
  let value = item
  for (const part of parts) {
    if (value == null) return ''
    value = value[part]
  }
  return value == null ? '' : value
}

const buildQueryString = (filters) => {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') params.append(k, v)
  })
  return params.toString()
}

async function multiFieldListFetch ({ store, apiUrl, setListMutation, baseFilters, searchFields, search, axiosConfig }) {
  const profile = store.getters['auth/profile']
  const resolvedUrl = (apiUrl || '').replace('{profile}', profile)
  const tokens = tokenize(search)
  if (!tokens.length) {
    const url = `${resolvedUrl}/?${buildQueryString({ ...baseFilters, per_page: 50 })}`
    const response = await axiosThux.get(url, axiosConfig)
    store.commit(setListMutation, response.data, { root: true })
    return response.data
  }
  const requests = []
  for (const token of tokens) {
    for (const field of searchFields) {
      const qs = buildQueryString({
        ...baseFilters,
        [`filter__${field}__icontains`]: token,
        per_page: 50
      })
      requests.push(axiosThux.get(`${resolvedUrl}/?${qs}`, axiosConfig))
    }
  }
  const settled = await Promise.allSettled(requests)
  const seen = new Set()
  const merged = []
  for (const r of settled) {
    if (r.status !== 'fulfilled') continue
    const results = (r.value.data && r.value.data.results) || []
    for (const item of results) {
      if (!seen.has(item.id)) {
        seen.add(item.id)
        merged.push(item)
      }
    }
  }
  const tokensLower = tokens.map((t) => t.toLowerCase())
  const filtered = merged.filter((item) =>
    tokensLower.every((tok) =>
      searchFields.some((f) => String(getFieldValue(item, f)).toLowerCase().includes(tok))
    )
  )
  const list = { count: filtered.length, results: filtered, per_page: filtered.length || 1 }
  store.commit(setListMutation, list, { root: true })
  return list
}

export default {
  name: 'TaskFormMixin',
  mixins: [TaskChoicesMixin],
  computed: {
    ...mapGetters({
      taskCategoryList: TASK_CATEGORY_TYPES.GENERIC.task['task-category'].LIST.GETTERS.list,
      priorityList: TASK_PRIORITY_TYPES.GENERIC.task['task-priority'].LIST.GETTERS.list,
      taskTagList: TASK_TAG_TYPES.GENERIC.task['task-tag'].LIST.GETTERS.list,
      userList: USER_TYPES.GENERIC.account.user.LIST.GETTERS.list,
      quotationList: QUOTATION_TYPES.GENERIC.quotation.quotation.LIST.GETTERS.list
    }),
    macroOptions () {
      return mapResultsToOptions(this.taskCategoryList, (item) => item.name)
    },
    taskPriorityOptions () {
      return mapResultsToOptions(this.priorityList, (item) => item.name).map((opt) => ({
        ...opt,
        color: opt.raw && opt.raw.color ? opt.raw.color : '#6c757d'
      }))
    },
    tagOptionsMulti () {
      if (!this.taskTagList || !this.taskTagList.results) return []
      return this.taskTagList.results
        .filter((item) => item.status !== 0)
        .map((item) => ({ id: item.id, name: item.name, status: item.status }))
    },
    assigneeOptionsMulti () {
      if (!this.userList || !this.userList.results) return []
      return this.userList.results
        .filter((item) => item.status !== 0)
        .map((item) => {
          const first = item.first_name || item.username || ''
          const last = item.last_name || ''
          const fullName = `${first} ${last}`.trim() || item.email || `#${item.id}`
          return { id: item.id, name: fullName.toUpperCase(), status: item.status }
        })
    },
    quotationOptionsMulti () {
      if (!this.quotationList || !this.quotationList.results) return []
      return this.quotationList.results
        .filter((item) => item.status !== 0)
        .map((item) => ({
          id: item.id,
          name: this.formatQuotationLabel(item),
          status: item.status
        }))
    }
  },
  methods: {
    ...mapActions({
      getTaskCategoryList: TASK_CATEGORY_TYPES.GENERIC.task['task-category'].LIST.ACTIONS.setFilters,
      getTaskPriorityListAction: TASK_PRIORITY_TYPES.GENERIC.task['task-priority'].LIST.ACTIONS.setFilters,
      createTaskTagAction: TASK_TAG_TYPES.GENERIC.task['task-tag'].DETAIL.ACTIONS.create,
      getUserListAction: USER_TYPES.GENERIC.account.user.LIST.ACTIONS.setFilters,
      getQuotationListAction: QUOTATION_TYPES.GENERIC.quotation.quotation.LIST.ACTIONS.setFilters
    }),
    ...mapMutations({
      setTaskCategoryFilters: TASK_CATEGORY_TYPES.GENERIC.task['task-category'].LIST.MUTATIONS.setList
    }),
    resolveCategoryTypeCode () {
      return (this.editParams && this.editParams.categoryTypeCode) ||
        (this.detailParams && this.detailParams.categoryTypeCode) ||
        'company'
    },
    fetchMacroOptions () {
      const typeCode = this.resolveCategoryTypeCode()
      this.setTaskCategoryFilters({})
      return this.getTaskCategoryList({ filter__status: 1, filter__type__code: typeCode })
    },
    fetchPriorityOptions () {
      return this.getTaskPriorityListAction({ filter__status: 1 })
    },
    fetchTagOptions (search) {
      // Autocomplete tag senza blockUI a tutto schermo: fetch diretto con
      // { noBlockUI: true } invece della store action, così la soppressione resta
      // su questa singola chiamata e non tocca la pagina admin dei tag.
      return multiFieldListFetch({
        store: this.$store,
        apiUrl: '/api/v1/task/{profile}/task-tag',
        setListMutation: TASK_TAG_TYPES.GENERIC.task['task-tag'].LIST.MUTATIONS.setList,
        baseFilters: { filter__status: 1 },
        searchFields: ['name'],
        search,
        axiosConfig: { noBlockUI: true }
      })
    },
    fetchAssigneeOptions (search) {
      const baseFilters = {
        filter__status: 1,
        filter__contact__organization_contacts__organization__organization_types__category: 'com',
        filter__contact__organization_contacts__status: 1,
        filter__contact__status: 1
      }
      const extra = this.editParams && this.editParams.assigneeFilters
      if (extra && typeof extra === 'object') {
        Object.assign(baseFilters, extra)
      }
      if (!search) return this.getUserListAction(baseFilters)
      return multiFieldListFetch({
        store: this.$store,
        apiUrl: '/api/v1/account/{profile}/user',
        setListMutation: USER_TYPES.GENERIC.account.user.LIST.MUTATIONS.setList,
        baseFilters,
        searchFields: ['first_name', 'last_name', 'email'],
        search
      })
    },
    fetchQuotationOptions (search) {
      const typeCode = this.resolveCategoryTypeCode()
      const orgId = (this.form && this.form.organization) ||
        (this.editParams && this.editParams.organization) ||
        (this.detailParams && this.detailParams.organization) ||
        null
      const baseFilters = { filter__status: 1 }
      if (orgId && typeCode !== 'company') baseFilters.filter__customer = orgId
      const isCustomerSubtask = !!(this.form && this.form.parent) && typeCode !== 'company'
      if (orgId && this.form && (this.form.is_external || isCustomerSubtask)) {
        baseFilters.filter__customer__id = orgId
      }
      if (!search) return this.getQuotationListAction(baseFilters)
      const searchFields = typeCode === 'company'
        ? ['number', 'customer__name']
        : ['description', 'number']
      return multiFieldListFetch({
        store: this.$store,
        apiUrl: '/api/v1/quotation/{profile}/quotation',
        setListMutation: QUOTATION_TYPES.GENERIC.quotation.quotation.LIST.MUTATIONS.setList,
        baseFilters,
        searchFields,
        search
      })
    },
    fetchCustomerOrganizationOptions (search) {
      const profile = this.$store.getters['auth/profile']
      const baseUrl = `/api/v1/organization/${profile}/customer-organization/`
      const tokens = tokenize(search)
      if (!tokens.length) {
        return axiosThux.get(`${baseUrl}?per_page=50`)
          .then((response) => (response.data && response.data.results) || [])
      }
      const requests = tokens.map((token) => {
        // In ricerca NON filtriamo per status: l'azienda va trovata anche se archiviata/non attiva.
        const qs = buildQueryString({
          filter__name__icontains: token,
          per_page: 50
        })
        return axiosThux.get(`${baseUrl}?${qs}`)
      })
      return Promise.allSettled(requests).then((settled) => {
        const seen = new Set()
        const merged = []
        for (const r of settled) {
          if (r.status !== 'fulfilled') continue
          const results = (r.value.data && r.value.data.results) || []
          for (const item of results) {
            if (!seen.has(item.id)) {
              seen.add(item.id)
              merged.push(item)
            }
          }
        }
        const tokensLower = tokens.map((t) => t.toLowerCase())
        return merged.filter((item) =>
          tokensLower.every((tok) => String(item.name || '').toLowerCase().includes(tok))
        )
      }).catch(() => [])
    },
    fetchOwnerOrganizationDefault () {
      const profile = this.$store.getters['auth/profile']
      const url = `/api/v1/organization/${profile}/owner-organization/?filter__status=1&per_page=1`
      return axiosThux.get(url)
        .then((response) => {
          const results = (response.data && response.data.results) || []
          return results.length ? results[0] : null
        })
        .catch(() => null)
    },
    formatQuotationLabel (item) {
      if (!item) return ''
      const typeCode = this.resolveCategoryTypeCode()
      const num = item.quotation_code || item.number || `#${item.id}`
      const desc = item.description || item.full_description || ''
      let base = desc ? `(${num}) - ${desc}` : `(${num})`
      if (typeCode === 'company') {
        const customerName = item.customer_name || (item.customer && item.customer.name) || ''
        if (customerName) base = `${customerName} - ${base} `
      }
      let status = this.$t('Non Accettato')
      if (item.order_done === true) {
        status = this.$t('Accettato')
      }
      return `${base} (${status})`
    },
    selectSingleToValue (event, fieldName, displayField) {
      this.$set(this.form, fieldName, event.value)
      if (displayField) {
        this.$set(this.form, displayField, event.text)
      }
    },
    clearSingle (fieldName, displayField) {
      this.$set(this.form, fieldName, null)
      if (displayField) {
        this.$set(this.form, displayField, null)
      }
    },
    multiselectIdList (selectedList) {
      if (!selectedList || selectedList.length === 0) return []
      return selectedList.map((item) => (typeof item === 'object' ? item.id : item))
    },
    ensureArray (value) {
      if (!value) return []
      if (Array.isArray(value)) return value
      return [value]
    }
  }
}
